import React from 'react';
import { useTranslation } from 'react-i18next';
import { Users, TrendingUp, DollarSign, Activity } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useCurrentUser, useFeatureFlag } from '@/hooks/redux';
import { useUsersQuery } from '@/store/api/usersAPi';
import Loader from '@/components/Loader';


interface MetricCardProps {
  title: string;
  value: string;
  icon: React.ComponentType<{ className?: string }>;
}

function MetricCard({ title, value, icon: Icon }: MetricCardProps) {
  return (
    <div
      className="
        whitespace-nowrap
        text-sm
        ring-offset-background
        focus-visible:outline-none
        focus-visible:ring-2
        focus-visible:ring-ring
        focus-visible:ring-offset-2
        disabled:pointer-events-none
        py-2
        w-full
        h-full
        font-semibold
        font-spectral
        text-black
        uppercase
        bg-white
        inner-shadow-blue
        rounded-lg
        cursor-pointer
        transition-all
        duration-500
        ease-in-out
        hover:bg-[linear-gradient(to_right,#082261,#34065c)]
        hover:text-white
      "
    >
      <div className="p-6">
        <div className="flex items-center justify-between pb-2">
          <p className="text-sm font-medium">{title}</p>
          <Icon className="h-4 w-4 opacity-80" />
        </div>

        <div className="text-2xl font-bold">{value}</div>
      </div>
    </div>
  );
}

export function DashboardPage() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const advancedAnalytics = useFeatureFlag('advancedAnalytics');

  const { data, isLoading } = useUsersQuery({
    take: 10,
    skip: 0,
  });

  const totalUsers = data?.count ?? 0;
  const activeUsers = data?.totalActive ?? 0;
  const blockedUsers = data?.totalBlocked ?? 0;

  const metrics = [
    {
      title: t('dashboard.totalUsers'),
      value: isLoading ? '—' : totalUsers.toString(),
      icon: Users,
    },
    {
      title: t('dashboard.activeUsers'),
      value: isLoading ? '—' : activeUsers.toString(),
      icon: Activity,
    },
    {
      title: 'Blocked Users',
      value: isLoading ? '—' : blockedUsers.toString(),
      icon: Users,
    },
  ];

  if (isLoading) {
  return (
    <div className="min-h-[60vh] flex items-center justify-center text-[#082261] font-spectral text-lg">
      <Loader />
    </div>
  );
}

return (        
  <div className="space-y-8">
    <div>
      <h2 className="text-3xl font-bold tracking-tight text-[#082261] font-spectral">
        {t('dashboard.welcome')}
      </h2>
      <p className="text-muted-foreground">
        {user?.name && `Welcome back, ${user.name}! `}
        Here's what's happening with your application today.
      </p>
    </div>

    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {metrics.map((metric, index) => (
        <MetricCard key={index} {...metric} />
      ))}
    </div>

    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
      <Card className="col-span-4">
        <CardHeader>
          <CardTitle>Overview</CardTitle>
          <CardDescription>Revenue and user growth over time</CardDescription>
        </CardHeader>

        <CardContent className="pl-2">
          <div className="h-[200px] flex items-center justify-center border-2 border-dashed rounded-lg">
            <TrendingUp className="h-8 w-8 text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
);
}